/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import type { PayloadError } from 'relay-runtime';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import HttpError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';

const LOG_NAMESPACE = 'relay.AGG.error.graphql';
const LOG_NAMESPACE_INFO = 'relay.AGG.info.logQueryFallbackToPost';

type GraphqlQueryDetails = {
	queryName: string;
	operationKind: string;
};

const errorLogFn = functionWithCondition(
	() => fg('jira_relay_fetch_use_log_unsafe'),
	log.unsafeErrorWithCustomerData,
	log.safeErrorWithoutCustomerData,
);

export const getLoggingAndMetricsHelper = (
	graphqlDetails: GraphqlQueryDetails,
): {
	logNonBreakingErrors: (errors?: PayloadError[] | null, requestId?: string) => void;
	logError: (error?: Error, retryAttempt?: number) => void;
	logErrorRetry: (error?: Error, retryAttempt?: number) => void;
	logQueryFallbackToPost: (reason: string) => void;
} => {
	const { queryName, operationKind } = graphqlDetails;
	const logErrorRetry = (error?: Error, retryAttempt?: number): void => {
		const statusCode = get(error, ['statusCode'], null);
		const errorType = error instanceof HttpError ? 'network' : 'unknown';

		// Log any errors even in SSR
		errorLogFn(LOG_NAMESPACE, `[${errorType} error]`, {
			message: `[${capitalize(errorType)} error]: ${error?.message ?? ''}`,
			errorType,
			statusCode,
			queryName,
			operationKind,
			errorName: get(error, ['name'], null),
			retryAttempt,
		});
	};

	return {
		logNonBreakingErrors: (errors?: PayloadError[] | null, requestId?: string): void => {
			if (__SERVER__ || isNil(errors) || !Array.isArray(errors)) {
				return;
			}
			errors.forEach((error) => {
				error &&
					errorLogFn(LOG_NAMESPACE, '[graphql error]', {
						queryName,
						operationKind,
						errorType: 'graphql',
						message: error.message,
						requestId,
					});
			});
		},
		logError: logErrorRetry,
		logErrorRetry,
		logQueryFallbackToPost: (reason: string): void => {
			if (__SERVER__ && reason === 'SSR') {
				return;
			}

			log.safeInfoWithoutCustomerData(LOG_NAMESPACE_INFO, 'logQueryFallbackToPost', {
				queryName,
				operationKind,
				reason,
			});
		},
	};
};
